import { useEffect, useSyncExternalStore } from 'react';

/**
 * The title the app's navigation bar shows for the current screen (T-0320).
 *
 * `Layout` knows it — the same words as the narrow header, `viewTitleForPath` — and the runtime that
 * posts `screen` sits above `Layout`, in the router's root. This is the hand-off between them. A
 * screen outside `Layout` sets nothing, and the runtime falls back to the document's title.
 */
let current: string | null = null;
const listeners = new Set<() => void>();

function publish(title: string | null): void {
  if (title === current) return;
  current = title;
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called by the screen that knows its own title; cleared when it unmounts. */
export function useShellScreenTitle(title: string): void {
  useEffect(() => {
    publish(title);
  }, [title]);
  useEffect(() => () => publish(null), []);
}

/** The title published by the screen on display, or `null`. */
export function useShellScreenTitleValue(): string | null {
  return useSyncExternalStore(subscribe, () => current);
}
